export const aswbSessionItems = [
	{
		type: "SET",
		stimulus: {
			id: 1,
			content: {
				instruction:
					"Watch the video and select the correct answer from the options provided.",
				passage: {
					title: "Social Work Case: Partner Violence Disclosure",
					paragraphs: [
						{
							text: "Client reports partner's escalating anger, physical shoving, and concerns about safety for herself and children.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi65umJq8HvT2FcoFjQV9z6O",
						url: "vi65umJq8HvT2FcoFjQV9z6O",
						type: "video",
						transcript: `Hi … I’m feeling a bit nervous sharing this, but I trust you.
My partner’s anger has been getting worse. Last night he shoved me during an argument and I fell against the kitchen counter. I have a small bruise on my arm.
He apologized this morning and promised it won’t happen again.
I don’t know if I’m overreacting. We have two kids, and I’m scared of breaking up our family.
What should I do?`,
					},
				],
			},
			metadata: {
				type: "READING",
				skill: "VIDEO",
				title: "Reading Stimulus",
			},
		},
		items: [
			{
				id: 10,
				type: "MCSS",
				content: {
					parentId: 1,
					parentType: "SET",
					stem: "Which immediate response best supports Leah’s safety and autonomy?",
					choices: [
						{
							id: "100f17b1-2f1e-4d07-af6b-f17de7857933",
							stem: "Validate her feelings and explore a safety plan",
							text: "Validate her feelings and explore a safety plan",
							isCorrect: true,
							explanation: `**Validating Leah’s feelings and exploring a safety plan** is the most appropriate immediate response in this scenario. **Validation acknowledges her experience and reduces isolation**, while collaboratively developing a safety plan empowers her to make informed choices about her well-being and her children’s safety. This approach respects her autonomy and addresses the urgent risk of harm without imposing decisions.

Other responses are **not the preferred initial choice**. **Recommending immediate divorce** may feel overwhelming and does not prioritize her current safety or readiness for such a step. **Advising couples counseling with her partner present** is contraindicated in situations involving intimate partner violence, as it can increase risk and minimize the seriousness of abuse. **Suggesting she wait to see if the behavior improves** fails to address the immediate safety concerns and may inadvertently reinforce the cycle of abuse.

For **an initial response to intimate partner violence disclosure**, **validation and safety planning are the most effective and ethical choices**.

**Key takeaways:**
- **Validation and safety planning are first-line interventions** for clients disclosing intimate partner violence.
- **Other interventions, such as couples counseling or urging immediate divorce, are not appropriate as initial steps** and may increase risk or undermine client autonomy.

**References:**
- National Association of Social Workers (NASW). (2021). *NASW Standards for Social Work Practice in Health Care Settings*. Retrieved from [https://www.socialworkers.org/Practice/Practice-Standards-Guidelines](https://www.socialworkers.org/Practice/Practice-Standards-Guidelines)
- National Domestic Violence Hotline. (2023). *How to support someone experiencing abuse*. Retrieved from [https://www.thehotline.org/support-others/](https://www.thehotline.org/support-others/)
- Warshaw, C., Lyon, E., Phillips, H., & Bland, P. (2014). *Mental health and substance use coercion surveys: Report from the National Center on Domestic Violence, Trauma & Mental Health*. Retrieved from [https://www.nationalcenterdvtraumamh.org/](https://www.nationalcenterdvtraumamh.org/)`,
							numFirstSelections: 146,
						},
						{
							id: "ea2fb96d-a000-435d-b5e1-5617853f0c54",
							stem: "Recommend filing for divorce immediately",
							text: "Recommend filing for divorce immediately",
							isCorrect: false,
							numFirstSelections: 42,
						},
						{
							id: "ea2fb96d-a000-435d-b5e1-5617853f0c54-ged",
							stem: "Advise couples counseling with her partner present",
							text: "Advise couples counseling with her partner present",
							isCorrect: false,
							numFirstSelections: 42,
						},
						{
							id: "9bd9f1ff-0001-4704-8fab-b98742b29e05",
							stem: "Suggest waiting to see if the behavior improves",
							text: "Suggest waiting to see if the behavior improves",
							isCorrect: false,
							numFirstSelections: 34,
						},
					],
					instruction: "Choose the best answer",
					highlights: null,
					media: null,
				},
				tags: [
					{
						id: 6479,
						type: "Skill",
						label: "Reading",
						breadcrumb: "Reading",
					},
				],
				metadata: {
					type: "MCSS",
					skill: "READING",
					title: "Multiple Choice Single Selection",
					topics: ["Social Work"],
				},
				response: {
					maximumScore: 1,
					correctResponse: [
						{
							id: "100f17b1-2f1e-4d07-af6b-f17de7857933",
						},
					],
					numChoicesRequired: {
						max: 1,
						min: 1,
					},
				},
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 2,
			content: {
				instruction:
					"Watch the video and write your answer in the text box provided.",
				passage: {
					title: "Social Work Case: Partner Violence Disclosure",
					paragraphs: [
						{
							text: "Client reports partner's escalating anger, physical shoving, and concerns about safety for herself and children.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi65umJq8HvT2FcoFjQV9z6O",
						url: "vi65umJq8HvT2FcoFjQV9z6O",
						type: "video",
						transcript: `Hi … I'm feeling a bit nervous sharing this, but I trust you.
My partner's anger has been getting worse. Last night he shoved me during an argument and I fell against the kitchen counter. I have a small bruise on my arm.
He apologized this morning and promised it won't happen again.
I don't know if I'm overreacting. We have two kids, and I'm scared of breaking up our family.
What should I do?`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_STIMULUS",
				skill: "VIDEO",
				title: "Integrated Writing Stimulus",
				topics: ["Social Work"],
			},
		},
		items: [
			{
				id: 20,
				versionId: 1,
				content: {
					parentId: 2,
					parentType: "SET",
					stem: `In 150 words or fewer, draft the opening of Leah's session note that documents:
- Her disclosure of physical abuse
- Your assessment of immediate risk
- The collaborative decision to begin a safety plan`,
					choices: [],
					instruction:
						"Watch the video and write your answer in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 15,
					subskillsSchema: [
						{
							name: "Documentation",
							icon: "file-document-outline",
							description:
								"Write clear, concise, and objective session notes that accurately reflect the client's disclosure and your clinical assessment.",
							maxScore: 5,
						},
						{
							name: "Risk",
							icon: "alert-decagram-outline",
							description:
								"Demonstrate the ability to assess and document immediate safety risks, including potential harm to the client or others.",
							maxScore: 5,
						},
						{
							name: "Collaboration",
							icon: "account-group-outline",
							description:
								"Show skill in documenting collaborative safety planning and shared decision-making with the client.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Integrated Writing Response",
					topics: ["Social Work"],
				},
				score: null,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 3,
			content: {
				instruction:
					"Watch the video and circle the factor that has been asked to be marked.",
				passage: {
					title: "Social Work Case: Partner Violence Disclosure",
					paragraphs: [
						{
							text: "Client reports partner's escalating anger, physical shoving, and concerns about safety for herself and children.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi65umJq8HvT2FcoFjQV9z6O",
						url: "vi65umJq8HvT2FcoFjQV9z6O",
						type: "video",
						transcript: `Hi … I'm feeling a bit nervous sharing this, but I trust you.
My partner's anger has been getting worse. Last night he shoved me during an argument and I fell against the kitchen counter. I have a small bruise on my arm.
He apologized this morning and promised it won't happen again.
I don't know if I'm overreacting. We have two kids, and I'm scared of breaking up our family.
What should I do?`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_MARKING_ANSWER_STIMULUS",
				skill: "VIDEO",
				title: "Integrated Marking Answer Stimulus",
				topics: ["Social Work"],
			},
		},
		items: [
			{
				id: 30,
				versionId: 1,
				content: {
					parentId: 3,
					parentType: "SET",
					stem: "Below is an image from the Danger Assessment instrument. Circle the factor that most increases Leah’s lethality risk.",
					choices: [],
					instruction:
						"Watch the video and circle the factor that most increases Leah’s lethality risk.",
					media: [
						{
							id: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/aswb_marking_image.png",
							url: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/aswb_marking_image.png",
							type: "image",
						},
					],
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Marking",
						breadcrumb: "Marking",
					},
				],
				response: {
					maximumScore: 10,
					subskillsSchema: [
						{
							name: "Accuracy",
							icon: "crosshairs-gps",
							maxScore: 5,
							description:
								"Improve your ability to precisely identify and mark abnormalities in medical imaging. Focus on developing keen attention to detail to ensure correct identification of pathological findings.",
						},
						{
							name: "Speed",
							icon: "timer-outline",
							maxScore: 5,
							description:
								"Enhance your efficiency in analyzing and annotating medical images while maintaining diagnostic accuracy. Work on reducing annotation time without compromising the quality of your assessment.",
						},
					],
				},
				metadata: {
					type: "MARKING_ANSWER",
					skill: "MARKING",
					title: "Marking Answer",
					topics: ["Social Work"],
				},
				score: null,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 4,
			content: {
				instruction: "Watch the video and record your answer.",
				passage: {
					title: "Social Work Case: Partner Violence Disclosure",
					paragraphs: [
						{
							text: "Client reports partner's escalating anger, physical shoving, and concerns about safety for herself and children.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi65umJq8HvT2FcoFjQV9z6O",
						url: "vi65umJq8HvT2FcoFjQV9z6O",
						type: "video",
						transcript: `Hi … I'm feeling a bit nervous sharing this, but I trust you.
My partner's anger has been getting worse. Last night he shoved me during an argument and I fell against the kitchen counter. I have a small bruise on my arm.
He apologized this morning and promised it won't happen again.
I don't know if I'm overreacting. We have two kids, and I'm scared of breaking up our family.
What should I do?`,
					},
				],
			},
			metadata: {
				type: "LS_STIMULUS",
				skill: "VIDEO",
				title: "Listen Speak Stimulus",
				topics: ["Social Work"],
			},
		},
		items: [
			{
				id: 40,
				type: "AUDIO_INPUT_EMOTION",
				content: {
					parentId: 4,
					parentType: "SET",
					stem: `Record a ≤60-second response to Leah that:

- Affirms her courage in sharing
- Introduces at least one local or national IPV resource (e.g., hotline, shelter)
- Outlines the next step of creating a personalized safety plan`,
					choices: [],
					instruction: "Watch the video and record your answer.",
					media: null,
				},
				tags: [
					{
						id: 6481,
						type: "Skill",
						label: "Speaking",
						breadcrumb: "Speaking",
					},
				],
				response: {
					maximumScore: 20,
					maximumDuration: 60,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Empathy",
							icon: "heart-outline",
							description:
								"Demonstrate genuine understanding and compassion for Leah’s situation, validating her feelings and concerns.",
						},
						{
							maxScore: 5,
							name: "Clarity",
							icon: "message-outline",
							description:
								"Communicate information and next steps in a clear, accessible, and supportive manner.",
						},
						{
							maxScore: 5,
							name: "Resources",
							icon: "map-marker-outline",
							description:
								"Provide accurate, relevant information about local or national IPV resources and ensure recommendations are appropriate.",
						},
						{
							maxScore: 5,
							name: "Professionalism",
							icon: "briefcase-outline",
							description:
								"Maintain professional demeanor and ethical standards while demonstrating cultural competence and respect.",
						},
					],
				},
				metadata: {
					type: "AUDIO_INPUT_EMOTION",
					skill: "SPEAKING",
					title: "Audio Input Item",
					topics: ["Social Work"],
				},
				score: 0,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 5,
			content: {
				instruction:
					"Watch the video and write your answer in the text box provided.",
				passage: {
					title: "Social Work Case: Partner Violence Disclosure",
					paragraphs: [
						{
							text: "Client reports partner's escalating anger, physical shoving, and concerns about safety for herself and children.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi65umJq8HvT2FcoFjQV9z6O",
						url: "vi65umJq8HvT2FcoFjQV9z6O",
						type: "video",
						transcript: `Hi … I'm feeling a bit nervous sharing this, but I trust you.
My partner's anger has been getting worse. Last night he shoved me during an argument and I fell against the kitchen counter. I have a small bruise on my arm.
He apologized this morning and promised it won't happen again.
I don't know if I'm overreacting. We have two kids, and I'm scared of breaking up our family.
What should I do?`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_STIMULUS",
				skill: "VIDEO",
				title: "Integrated Writing Stimulus",
				topics: ["Social Work"],
			},
		},
		items: [
			{
				id: 50,
				versionId: 1,
				content: {
					parentId: 5,
					parentType: "SET",
					stem: "Document the Safety-Plan Summary for this client. Your documentation must include: risk level, interventions chosen, follow-up appointment date, consent for collateral contact, and safety-plan review date.",
					choices: [],
					instruction:
						"Watch the video and write your answer in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 20,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Clarity",
							icon: "file-document-outline",
							description: "Clear and objective documentation.",
						},
						{
							maxScore: 5,
							name: "Assessment",
							icon: "alert-decagram-outline",
							description: "Accurate recording of risk and needs.",
						},
						{
							maxScore: 5,
							name: "Intervention",
							icon: "account-group-outline",
							description: "Summary of actions and plans.",
						},
						{
							maxScore: 5,
							name: "Compliance",
							icon: "check-circle-outline",
							description: "Meets professional standards.",
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Integrated Writing Response",
					topics: ["Social Work"],
					taskType: "default",
				},
				score: null,
			},
		],
	},
];
